import React, { useState, useEffect, useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import "./EditReportSketchDesignModal.css";
import { getSketchDesignEstimateWorkTypes } from "../../../api/projectEstimatesApi";

registerLocale("ru", ru);

const createEffortOptions = () => {
  const options = [];
  for (let hours = 0; hours <= 8; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 15) {
      if (hours === 0 && minutes === 0) continue;
      if (hours === 8 && minutes > 0) break;
      const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      options.push({ value, label: value });
    }
  }
  return options;
};

const EFFORT_OPTIONS = createEffortOptions();

const secondsSpendToEffort = (secondsSpend) => {
  if (secondsSpend == null) return "00:00";
  const totalMinutes = Math.floor(Number(secondsSpend) / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const effortToSecondsSpend = (effort) => {
  const [hours, minutes] = effort.split(":").map(Number);
  return hours * 3600 + minutes * 60;
};

const EditReportSketchDesignModal = ({
  report,
  projects,
  workTypesByProject,
  onClose,
  onSave,
  onDelete,
  error,
}) => {
  const [datetime, setDatetime] = useState(() =>
    report?.datetime ? new Date(report.datetime) : new Date()
  );
  const [projectId, setProjectId] = useState(report?.project_id ?? "");
  const [workTypeId, setWorkTypeId] = useState(report?.work_type_id ?? "");
  const [effort, setEffort] = useState(
    secondsSpendToEffort(report?.seconds_spend) || "00:15"
  );
  const [comment, setComment] = useState(report?.comment ?? "");
  const [isRemote, setIsRemote] = useState(!!report?.is_remote);
  const [workTypes, setWorkTypes] = useState(
    () => workTypesByProject[report?.project_id] || []
  );
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setWorkTypes([]);
      setWorkTypeId("");
      return;
    }
    const cached = workTypesByProject[projectId];
    if (cached && cached.length >= 0) {
      setWorkTypes(cached);
      setWorkTypeId((prev) => {
        const exists = cached.some((wt) => wt.id === prev);
        return exists ? prev : "";
      });
      return;
    }
    let cancelled = false;
    setLoadingWorkTypes(true);
    getSketchDesignEstimateWorkTypes(parseInt(projectId, 10))
      .then((raw) => {
        if (cancelled) return;
        const list = Array.isArray(raw) ? raw : raw?.work_types ?? [];
        const items = Array.isArray(list) ? list : [];
        setWorkTypes(items);
        setWorkTypeId((prev) => {
          const exists = items.some((wt) => wt.id === prev);
          return exists ? prev : (items[0]?.id ?? "");
        });
      })
      .catch(() => {
        if (!cancelled) setWorkTypes([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingWorkTypes(false);
      });
    return () => { cancelled = true; };
  }, [projectId, workTypesByProject]);

  const projectOptions = useMemo(
    () =>
      projects.map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [projects]
  );

  const workTypeOptions = useMemo(
    () =>
      workTypes
        .filter((wt) => (wt.status || "").toLowerCase() !== "completed")
        .map((wt) => ({
          value: wt.id,
          label: wt.name,
        })),
    [workTypes]
  );

  const isFormValid = () =>
    datetime &&
    projectId &&
    workTypeId &&
    effort &&
    effort !== "00:00";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const dateStr = datetime.toISOString().split("T")[0];
      const payload = {
        datetime: dateStr,
        project_id: parseInt(projectId, 10),
        work_type_id: parseInt(workTypeId, 10),
        comment: comment.trim(),
        seconds_spend: effortToSecondsSpend(effort),
        is_remote: isRemote,
      };
      await onSave(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (
      !onDelete ||
      !window.confirm("Удалить этот отчёт ЭП?") ||
      isDeleting ||
      isSubmitting
    ) {
      return;
    }
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      minHeight: "40px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
  };

  return (
    <div className="edit-report-sketch-modal-overlay" onClick={onClose}>
      <div
        className="edit-report-sketch-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="edit-report-sketch-modal__title">
          Редактировать отчёт
        </h2>
        {error && (
          <div className="edit-report-sketch-modal__error">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="edit-report-sketch-modal-form">
          <div className="edit-report-sketch-modal__row">
            <label>Дата</label>
            <DatePicker
              selected={datetime}
              onChange={(d) => setDatetime(d)}
              dateFormat="dd.MM.yyyy"
              locale={ru}
              className="edit-report-sketch-modal__input"
            />
          </div>
          <div className="edit-report-sketch-modal__row">
            <label>Проект</label>
            <Select
              options={projectOptions}
              value={projectOptions.find((o) => o.value === parseInt(projectId, 10)) ?? null}
              onChange={(opt) => {
                setProjectId(opt ? opt.value : "");
                setWorkTypeId("");
              }}
              placeholder="Выберите проект"
              styles={selectStyles}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="edit-report-sketch-modal__row">
            <label>Вид работ</label>
            <Select
              options={workTypeOptions}
              value={workTypeOptions.find((o) => o.value === parseInt(workTypeId, 10)) ?? null}
              onChange={(opt) => setWorkTypeId(opt ? opt.value : "")}
              placeholder={
                loadingWorkTypes
                  ? "Загрузка..."
                  : "Выберите вид работ"
              }
              isDisabled={!projectId || loadingWorkTypes}
              styles={selectStyles}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="edit-report-sketch-modal__row">
            <label>Часы</label>
            <Select
              options={EFFORT_OPTIONS}
              value={EFFORT_OPTIONS.find((o) => o.value === effort) ?? null}
              onChange={(opt) => setEffort(opt ? opt.value : "00:00")}
              placeholder="00:00"
              styles={{ ...selectStyles, control: (p) => ({ ...selectStyles.control(p), width: "120px" }) }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="edit-report-sketch-modal__row">
            <label>Комментарий</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="edit-report-sketch-modal__textarea"
              rows={3}
            />
          </div>
          <div className="edit-report-sketch-modal__row edit-report-sketch-modal__row--checkboxes">
            <label className="edit-report-sketch-modal__checkbox-label">
              <input
                type="checkbox"
                checked={isRemote}
                onChange={(e) => setIsRemote(e.target.checked)}
              />
              Удалённо
            </label>
          </div>
          <div className="edit-report-sketch-modal-buttons">
            {onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isSubmitting || isDeleting}
                className="edit-report-sketch-modal__btn edit-report-sketch-modal__btn--danger"
              >
                {isDeleting ? "Удаление..." : "Удалить"}
              </button>
            )}
            <div className="edit-report-sketch-modal-buttons__right">
              <button
                type="button"
                onClick={onClose}
                className="edit-report-sketch-modal__btn edit-report-sketch-modal__btn--secondary"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={!isFormValid() || isSubmitting || isDeleting}
                className="edit-report-sketch-modal__btn edit-report-sketch-modal__btn--primary"
              >
                {isSubmitting ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditReportSketchDesignModal;
